import React, { useState, useRef, useEffect, useCallback } from "react";
import { PALETTE, INSTRUMENTS } from "./constants";

// ─── COLOR PALETTE — full 14×10 grid ─────────────────────────

// ─── COLOR PICKER — fixed/portal positioning, never clipped ──
const ColorPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState(value || '#000000');
  const [pickerPos, setPickerPos] = useState({ top:0, left:0, above:false });
  const swatchRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => { setHex(value || '#000000'); }, [value]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (swatchRef.current?.contains(e.target)) return;
      if (popupRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const openPicker = () => {
    if (!swatchRef.current) return;
    const r = swatchRef.current.getBoundingClientRect();
    const PW = 268, PH = 240;
    let top = r.bottom + 8;
    let left = r.left;
    const above = top + PH > window.innerHeight - 16;
    if (above) top = r.top - PH - 8;
    if (left + PW > window.innerWidth - 12) left = window.innerWidth - PW - 12;
    if (left < 8) left = 8;
    setPickerPos({ top, left, above });
    setOpen(o => !o);
  };

  const pick = (c) => { onChange(c); setHex(c); setOpen(false); };

  return (
    <>
      <div ref={swatchRef} onClick={openPicker} title={value} style={{
        width:28, height:22, background:value, border:"2px solid rgba(255,255,255,0.22)",
        borderRadius:3, cursor:"pointer", flexShrink:0,
        boxShadow:"inset 0 1px 3px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.3)",
      }}/>
      {open && (
        <div ref={popupRef} onClick={e=>e.stopPropagation()} style={{
          position:"fixed", top:pickerPos.top, left:pickerPos.left,
          zIndex:999999, background:"#1a1d27",
          border:"1px solid rgba(255,255,255,0.2)",
          borderRadius:6, padding:10,
          boxShadow:"0 20px 60px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.05)",
          width:268,
        }}>
          {/* Color grid */}
          <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
            {PALETTE.map((row, ri) => (
              <div key={ri} style={{ display:"flex", gap:2 }}>
                {row.map((c, ci) => {
                  const selected = c.toLowerCase() === (value||'').toLowerCase();
                  return (
                    <div key={ci} onClick={() => pick(c)} title={c} style={{
                      width:16, height:16, background:c, borderRadius:2,
                      cursor:"pointer", flexShrink:0, boxSizing:"border-box",
                      border: selected ? "2px solid #ffffff" : "1px solid rgba(0,0,0,0.25)",
                      outline: selected ? "1px solid rgba(0,0,0,0.6)" : "none",
                      transition:"transform 0.07s",
                    }}
                      onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.35)";e.currentTarget.style.zIndex=10;e.currentTarget.style.position="relative";}}
                      onMouseLeave={e=>{e.currentTarget.style.transform="scale(1)";e.currentTarget.style.zIndex=1;}}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          {/* Hex input */}
          <div style={{ marginTop:10, borderTop:"1px solid rgba(255,255,255,0.1)", paddingTop:8, display:"flex", gap:6, alignItems:"center" }}>
            <div style={{ width:22, height:22, background:hex, borderRadius:3, border:"1px solid rgba(255,255,255,0.2)", flexShrink:0 }}/>
            <div style={{ display:"flex", flex:1, background:"#0d1117", border:"1px solid rgba(255,255,255,0.12)", borderRadius:3, overflow:"hidden", alignItems:"center" }}>
              <span style={{ padding:"0 4px 0 6px", fontFamily:"monospace", fontSize:11, color:"#444", lineHeight:1 }}>#</span>
              <input
                value={hex.replace(/^#/,'')}
                onChange={e => setHex('#' + e.target.value.replace(/[^0-9a-fA-F]/g,'').slice(0,6))}
                onKeyDown={e => { if(e.key==='Enter') pick(hex); }}
                maxLength={6}
                style={{ flex:1, background:"transparent", border:"none", color:"#ccc", fontFamily:"monospace", fontSize:11, padding:"4px 4px 4px 0", outline:"none", width:0 }}
              />
            </div>
            <div onClick={() => pick(hex)} style={{
              padding:"4px 12px", background:"#00C896", color:"#000",
              fontSize:11, fontWeight:700, borderRadius:3, cursor:"pointer",
              flexShrink:0, fontFamily:"'IBM Plex Sans',sans-serif",
            }}>OK</div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── SETTINGS MODAL ───────────────────────────────────────────
const SECTIONS = ["Symbol","Status Line","Scales & Lines","Canvas","Candles","Volume","Trading","Alerts","Events"];
const SEC_ICONS = {"Symbol":"◈","Status Line":"≡","Scales & Lines":"⊹","Canvas":"▣","Candles":"▮","Volume":"▦","Trading":"⚡","Alerts":"◎","Events":"◷"};

const SettingsModal = ({ settings, onChange, onClose, symbol, timeframe }) => {
  const [section, setSection] = useState("Canvas");
  const [draft, setDraft] = useState({...settings});
  const set = (k,v) => setDraft(d=>({...d,[k]:v}));

  const SH = ({title}) => <div style={{ fontSize:9, color:"#444", letterSpacing:"0.15em", textTransform:"uppercase", fontFamily:"monospace", padding:"14px 0 6px", marginTop:4 }}>{title}</div>;

  const Row = ({label, children}) => (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
      <span style={{ fontSize:13, color:"#bbb", fontFamily:"'IBM Plex Sans',sans-serif" }}>{label}</span>
      <div style={{ display:"flex", gap:8, alignItems:"center" }}>{children}</div>
    </div>
  );

  const Sel = ({value, opts, onChange:oc}) => (
    <select value={value} onChange={e=>oc(e.target.value)} style={{ background:"#141414", border:"1px solid rgba(255,255,255,0.1)", color:"#bbb", fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, padding:"4px 8px", borderRadius:3, outline:"none", minWidth:130, cursor:"pointer" }}>
      {opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );

  const Num = ({value, onChange:oc, unit, min=0, max=100}) => (
    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
      <input type="number" value={value} min={min} max={max} onChange={e=>oc(Number(e.target.value))} style={{ width:58, background:"#141414", border:"1px solid rgba(255,255,255,0.1)", color:"#bbb", fontFamily:"monospace", fontSize:12, padding:"4px 6px", borderRadius:3, outline:"none", textAlign:"right" }}/>
      {unit&&<span style={{fontSize:11,color:"#444"}}>{unit}</span>}
    </div>
  );

  const Toggle = ({value, onChange:oc}) => (
    <div onClick={()=>oc(!value)} style={{ width:34, height:18, borderRadius:9, background:value?"#00C896":"#2a2a2a", position:"relative", cursor:"pointer", transition:"background 0.2s", flexShrink:0 }}>
      <div style={{ position:"absolute", top:2, left:value?16:2, width:14, height:14, borderRadius:7, background:"#fff", transition:"left 0.2s" }}/>
    </div>
  );

  const content = () => {
    switch(section) {
      case "Symbol": return <>
        <SH title="Symbol Info"/>
        <Row label="Symbol"><span style={{fontFamily:"monospace",color:"#00C896",fontSize:14,fontWeight:700}}>{symbol}</span></Row>
        <Row label="Full Name"><span style={{color:"#666",fontSize:12}}>{INSTRUMENTS[symbol]?.name}</span></Row>
        <Row label="Exchange"><span style={{fontFamily:"monospace",color:"#666",fontSize:12}}>{INSTRUMENTS[symbol]?.exchange}</span></Row>
        <Row label="Timeframe"><span style={{fontFamily:"monospace",color:"#bbb",fontSize:13}}>{timeframe}</span></Row>
        <Row label="Tick Size"><span style={{fontFamily:"monospace",color:"#666",fontSize:12}}>{INSTRUMENTS[symbol]?.tickSize}</span></Row>
        <Row label="Tick Value"><span style={{fontFamily:"monospace",color:"#666",fontSize:12}}>${INSTRUMENTS[symbol]?.tickVal}/tick</span></Row>
      </>;
      case "Status Line": return <>
        <SH title="Status Line"/>
        <Row label="Show OHLC"><Toggle value={draft.showOHLC} onChange={v=>set("showOHLC",v)}/></Row>
        <Row label="Show volume"><Toggle value={draft.showVolInStatus} onChange={v=>set("showVolInStatus",v)}/></Row>
        <Row label="Show price change"><Toggle value={draft.showChangeInStatus} onChange={v=>set("showChangeInStatus",v)}/></Row>
      </>;
      case "Scales & Lines": return <>
        <SH title="Price Scale"/>
        <Row label="Show price scale"><Toggle value={draft.showPriceScale} onChange={v=>set("showPriceScale",v)}/></Row>
        <Row label="Show time scale"><Toggle value={draft.showTimeScale} onChange={v=>set("showTimeScale",v)}/></Row>
        <Row label="Scale position"><Sel value={draft.priceScalePosition} onChange={v=>set("priceScalePosition",v)} opts={[{v:"right",l:"Right"},{v:"left",l:"Left"}]}/></Row>
        <Row label="Auto scale"><Toggle value={draft.autoScale} onChange={v=>set("autoScale",v)}/></Row>
      </>;
      case "Canvas": return <>
        <SH title="Chart Basic Styles"/>
        <Row label="Background">
          <Sel value={draft.bgStyle} onChange={v=>set("bgStyle",v)} opts={[{v:"solid",l:"Solid"},{v:"gradient",l:"Gradient"}]}/>
          <ColorPicker value={draft.bgColor} onChange={v=>set("bgColor",v)}/>
        </Row>
        <Row label="Grid lines">
          <Sel value={draft.gridLines} onChange={v=>set("gridLines",v)} opts={[{v:"none",l:"None"},{v:"solid",l:"Solid"},{v:"dashed",l:"Dashed"}]}/>
        </Row>
        <Row label="Crosshair">
          <Sel value={draft.crosshairStyle} onChange={v=>set("crosshairStyle",v)} opts={[{v:"cross",l:"Cross"},{v:"dot",l:"Dot"},{v:"none",l:"None"}]}/>
          <ColorPicker value={draft.crosshairColor} onChange={v=>set("crosshairColor",v)}/>
        </Row>
        <Row label="Watermark">
          <Sel value={draft.watermark} onChange={v=>set("watermark",v)} opts={[{v:"hidden",l:"Hidden"},{v:"symbol",l:"Symbol"},{v:"clearfund",l:"ClearFund"}]}/>
          <ColorPicker value={draft.watermarkColor} onChange={v=>set("watermarkColor",v)}/>
        </Row>
        <SH title="Scales"/>
        <Row label="Text">
          <ColorPicker value={draft.scaleTextColor} onChange={v=>set("scaleTextColor",v)}/>
          <Num value={draft.scaleTextSize} onChange={v=>set("scaleTextSize",v)} min={7} max={16}/>
        </Row>
        <Row label="Lines"><ColorPicker value={draft.scaleLinesColor} onChange={v=>set("scaleLinesColor",v)}/></Row>
        <SH title="Margins"/>
        <Row label="Top"><Num value={draft.marginTop} onChange={v=>set("marginTop",v)} unit="%" min={0} max={50}/></Row>
        <Row label="Bottom"><Num value={draft.marginBottom} onChange={v=>set("marginBottom",v)} unit="%" min={0} max={50}/></Row>
        <Row label="Right"><Num value={draft.marginRight} onChange={v=>set("marginRight",v)} unit="bars" min={0} max={100}/></Row>
      </>;
      case "Candles": return <>
        <SH title="Chart Type"/>
        <Row label="Style">
          <Sel value={draft.candleStyle} onChange={v=>set("candleStyle",v)} opts={[
            {v:"candle",l:"Candles"},{v:"hollow",l:"Hollow Candles"},
            {v:"bar",l:"OHLC Bars"},{v:"line",l:"Line"},{v:"area",l:"Area"},
          ]}/>
        </Row>
        <SH title="Up Candle"/>
        <Row label="Body color"><ColorPicker value={draft.upColor} onChange={v=>set("upColor",v)}/></Row>
        <Row label="Border color"><ColorPicker value={draft.upBorderColor} onChange={v=>set("upBorderColor",v)}/></Row>
        <Row label="Wick color"><ColorPicker value={draft.upWickColor} onChange={v=>set("upWickColor",v)}/></Row>
        <SH title="Down Candle"/>
        <Row label="Body color"><ColorPicker value={draft.downColor} onChange={v=>set("downColor",v)}/></Row>
        <Row label="Border color"><ColorPicker value={draft.downBorderColor} onChange={v=>set("downBorderColor",v)}/></Row>
        <Row label="Wick color"><ColorPicker value={draft.downWickColor} onChange={v=>set("downWickColor",v)}/></Row>
        <SH title="Dimensions"/>
        <Row label="Border width"><Num value={draft.borderWidth} onChange={v=>set("borderWidth",v)} unit="px" min={0} max={4}/></Row>
        <Row label="Wick width"><Num value={draft.wickWidth} onChange={v=>set("wickWidth",v)} unit="px" min={0} max={4}/></Row>
      </>;
      case "Volume": return <>
        <SH title="Volume"/>
        <Row label="Show volume"><Toggle value={draft.showVolume} onChange={v=>set("showVolume",v)}/></Row>
        <Row label="Up color"><ColorPicker value={draft.volumeUpColor} onChange={v=>set("volumeUpColor",v)}/></Row>
        <Row label="Down color"><ColorPicker value={draft.volumeDownColor} onChange={v=>set("volumeDownColor",v)}/></Row>
      </>;
      case "Trading": return <>
        <SH title="Position Display"/>
        <Row label="Show position lines"><Toggle value={draft.showPositionLines} onChange={v=>set("showPositionLines",v)}/></Row>
        <Row label="Show P&L on chart"><Toggle value={draft.showPnlOnChart} onChange={v=>set("showPnlOnChart",v)}/></Row>
        <Row label="Position line color"><ColorPicker value={draft.positionLineColor} onChange={v=>set("positionLineColor",v)}/></Row>
      </>;
      case "Alerts": return <>
        <SH title="Alert Lines"/>
        <Row label="Line color"><ColorPicker value={draft.alertLineColor} onChange={v=>set("alertLineColor",v)}/></Row>
        <Row label="Line style"><Sel value={draft.alertLineStyle} onChange={v=>set("alertLineStyle",v)} opts={[{v:"solid",l:"Solid"},{v:"dashed",l:"Dashed"},{v:"dotted",l:"Dotted"}]}/></Row>
      </>;
      case "Events": return <>
        <SH title="Chart Events"/>
        <div style={{padding:"20px 0",color:"#333",fontFamily:"monospace",fontSize:11,textAlign:"center"}}>Events overlay coming soon.<br/>Earnings · Economic data · News markers.</div>
      </>;
      default: return null;
    }
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9500, background:"rgba(0,0,0,0.8)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center" }}
      onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={{ background:"#1a1a1a", border:"1px solid rgba(255,255,255,0.12)", borderRadius:6, width:620, maxHeight:"88vh", display:"flex", flexDirection:"column", boxShadow:"0 24px 64px rgba(0,0,0,0.95)" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", borderBottom:"1px solid rgba(255,255,255,0.07)" }}>
          <span style={{ fontFamily:"'IBM Plex Sans',sans-serif", fontSize:15, fontWeight:600, color:"#e8e8e8" }}>Settings</span>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"#555", cursor:"pointer", fontSize:20, lineHeight:1, padding:"2px 6px", borderRadius:3, fontFamily:"monospace" }}>×</button>
        </div>
        {/* Body */}
        <div style={{ display:"flex", flex:1, overflow:"hidden" }}>
          {/* Nav */}
          <div style={{ width:166, borderRight:"1px solid rgba(255,255,255,0.06)", padding:"6px 0", overflowY:"auto", flexShrink:0, background:"#141414" }}>
            {SECTIONS.map(sec=>(
              <div key={sec} onClick={()=>setSection(sec)} style={{
                display:"flex", alignItems:"center", gap:10, padding:"8px 16px",
                fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12,
                color:section===sec?"#e8e8e8":"#555",
                background:section===sec?"#1f1f1f":"transparent",
                borderLeft:section===sec?"2px solid #00C896":"2px solid transparent",
                cursor:"pointer", transition:"all 0.1s",
              }}>
                <span style={{fontSize:13,width:16,textAlign:"center"}}>{SEC_ICONS[sec]}</span>
                {sec}
              </div>
            ))}
          </div>
          {/* Content */}
          <div style={{ flex:1, overflowY:"auto", padding:"4px 22px 20px" }}>
            {content()}
          </div>
        </div>
        {/* Footer */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 20px", borderTop:"1px solid rgba(255,255,255,0.07)", background:"#141414" }}>
          <select style={{ background:"#1a1a1a", border:"1px solid rgba(255,255,255,0.08)", color:"#666", fontFamily:"'IBM Plex Sans',sans-serif", fontSize:11, padding:"4px 8px", borderRadius:3, outline:"none", cursor:"pointer" }}>
            <option>Template</option><option>Dark Default</option><option>Light</option>
          </select>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={onClose} style={{ padding:"7px 18px", background:"transparent", border:"1px solid rgba(255,255,255,0.1)", color:"#777", fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, borderRadius:3, cursor:"pointer" }}>Cancel</button>
            <button onClick={()=>{ onChange(draft); onClose(); }} style={{ padding:"7px 18px", background:"#00C896", color:"#000", fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12, fontWeight:700, border:"none", borderRadius:3, cursor:"pointer" }}>Ok</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── CONTEXT MENU ─────────────────────────────────────────────
const ContextMenu = ({ x, y, onClose, onSettings, onHLine, onTrendline, onClearDrawings, onResetZoom, onSetAlert }) => {
  const ref = useRef(null);
  useEffect(() => {
    const h = () => onClose();
    setTimeout(()=>document.addEventListener('click',h),0);
    return ()=>document.removeEventListener('click',h);
  }, []);

  const items = [
    { label:"Chart Settings…", icon:"⚙", action:onSettings },
    null,
    { label:"Add Horizontal Line", icon:"—", action:onHLine },
    { label:"Add Trendline", icon:"╱", action:onTrendline },
    { label:"Clear All Drawings", icon:"✕", action:onClearDrawings },
    null,
    { label:"Reset Zoom / Scroll", icon:"⊡", action:onResetZoom },
    null,
    { label:"Set Alert at Price", icon:"🔔", action:onSetAlert },
  ];

  return (
    <div ref={ref} style={{
      position:"fixed", left:x, top:y, zIndex:9000,
      background:"#1e1e1e", border:"1px solid rgba(255,255,255,0.12)",
      borderRadius:4, minWidth:200, boxShadow:"0 10px 36px rgba(0,0,0,0.8)",
      padding:"4px 0", fontFamily:"'IBM Plex Sans',sans-serif", fontSize:12,
    }} onClick={e=>e.stopPropagation()}>
      {items.map((item,i) => item===null ? (
        <div key={i} style={{ height:1, background:"rgba(255,255,255,0.07)", margin:"3px 0" }}/>
      ) : (
        <div key={i} onClick={()=>{ item.action(); onClose(); }} style={{
          display:"flex", alignItems:"center", gap:10, padding:"7px 14px", color:"#bbb", cursor:"pointer",
        }}
          onMouseEnter={e=>e.currentTarget.style.background="#2a2a2a"}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}
        >
          <span style={{ width:14, textAlign:"center", color:"#555", fontSize:13 }}>{item.icon}</span>
          {item.label}
        </div>
      ))}
    </div>
  );
};


export { ColorPicker, SettingsModal };
